import ConfirmModal from '@creator/ui/components/ConfirmModal/ConfirmModal';
import RadioButton from '@creator/ui/components/RadioButton/RadioButton';
import Text from '@creator/ui/components/Text/Text';
import { convertFirebaseTimestampToDate, fromDateTimeLocal, toDateTimeLocal } from '@creator/ui/utils/date-utils';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { getUserId } from '@src/model/user/helpers';
import { PostStatus } from '@src/store/contentEntities/model';
import { type ManageDraftPostAction, manageDraftPost } from '@src/store/posts/draft';
import { usePost } from '@src/store/posts/hooks/usePost';
import type { FC } from 'react';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SchedulePostInput from '../Post/SchedulePostInput/SchedulePostInput';

export interface ManageDraftPostModalProps {
    className?: string;
    tokenName: string;
    postId: string;
}

const actions: ManageDraftPostAction[] = ['publishNow', 'draft', 'schedule', 'discardDraft'];

const ManageDraftPostModal: FC<ManageDraftPostModalProps> = props => {
    const { postId, tokenName } = props;

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState<ManageDraftPostAction>();
    const [newScheduledAt, setNewScheduledAt] = useState<string>();
    const [error, setError] = useState<string>();
    const post = usePost(postId);

    const { handleModal } = useContext<UseModal>(ModalContext);

    function mapError(error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('input validation errors')) return setError('invalidScheduledDate');
        alert(`${t('generalErrorMessage')} ${errorMessage}`);
    }

    async function manageSchedule() {
        if (!post || !selectedAction) return;

        try {
            setIsLoading(true);
            await manageDraftPost({
                action: selectedAction,
                postId,
                tokenName,
                userId: getUserId(),
                scheduledAt: selectedAction === 'schedule' && newScheduledAt ? fromDateTimeLocal(newScheduledAt) : undefined,
            });
            setIsLoading(false);
            handleModal(null);
        } catch (error) {
            setIsLoading(false);
            console.error(error);
            mapError(error);
        }
    }

    function getTitle() {
        if (!post) return '';
        if (selectedAction === 'discardDraft') return t('manageDraftPost.discard.title');
        if (post.status === PostStatus.PUBLISHED_SCHEDULED) return t('manageDraftPost.scheduled.title');
        return t('manageDraftPost.draft.title');
    }

    function getSchedulePostInputValue() {
        if (!post) return '';
        if (newScheduledAt) return newScheduledAt;

        const { scheduledAt } = post;
        if (!scheduledAt) return '';
        const initialScheduledAt = convertFirebaseTimestampToDate(scheduledAt);
        const initialScheduledAtDateTimeLocal = toDateTimeLocal(initialScheduledAt.toISOString());
        return initialScheduledAtDateTimeLocal;
    }

    function getSubtitle() {
        if (!post) return '';
        if (selectedAction === 'discardDraft') return t('manageDraftPost.discard.subtitle');
        if (post.status === PostStatus.PUBLISHED_SCHEDULED) return t('manageDraftPost.scheduled.subtitle');
        return t('manageDraftPost.draft.subtitle');
    }

    function renderActionBody(action: ManageDraftPostAction) {
        if (action === 'schedule') return renderSchedulePostInput();
        return (
            <Text enableMultiline={true} className="block text-xs leading-none text-gray-500">
                {t(`manageDraftPost.action.${action}Helptip`)}
            </Text>
        );
    }

    function renderError() {
        if (!error) return null;
        return <Text className="text-xs text-red-500">{t(`ManageDraftPost.validation.${error}`)}</Text>;
    }

    function renderSchedulePostInput() {
        if (!post) return null;
        const vlaue = getSchedulePostInputValue();
        return (
            <Fragment>
                <SchedulePostInput className="text-sm" value={vlaue} onChange={setNewScheduledAt} isDisabled={selectedAction !== 'schedule'} />
                {renderError()}
            </Fragment>
        );
    }

    function renderAction(action: ManageDraftPostAction) {
        if (!post) return null;
        if (action === 'draft' && post.status === PostStatus.DRAFT) return null;
        return (
            <div key={action} className="flex space-x-4 cursor-pointer max-w-max" onClick={() => setSelectedAction(action)}>
                <RadioButton isActive={selectedAction === action} />
                <div className="space-y-2">
                    <Text className="block font-medium">{t(`manageDraftPost.action.${action}`)}</Text>
                    {renderActionBody(action)}
                </div>
            </div>
        );
    }

    function renderActions() {
        return actions.map(renderAction);
    }

    function renderContent() {
        return <div className="pt-8 space-y-6">{renderActions()}</div>;
    }

    return (
        <ConfirmModal
            title={getTitle()}
            subtitle={getSubtitle()}
            onClick={() => handleModal(null)}
            cancelButtonProps={{
                onClick: () => handleModal(null),
            }}
            okButtonProps={{
                isDisabled: !selectedAction || (selectedAction === 'schedule' && !newScheduledAt),
                onClick: () => manageSchedule(),
                isLoading,
            }}
            {...(selectedAction === 'discardDraft' && {
                okButtonText: t('manageDraftPost.action.discardDraft'),
            })}
        >
            {renderContent()}
        </ConfirmModal>
    );
};

export default ManageDraftPostModal;
